import React, { useEffect, useRef, useState } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { useEstabFormContext } from './Context';
import { SettingsRow, SettingsSection } from './SettingsGroup';
import { Smile } from 'lucide-react';

const MESSAGE_VARIABLES = [
    { key: 'cliente', example: 'João Silva' },
    { key: 'estabelecimento', example: 'Barbearia Central' },
    { key: 'servico', example: 'Corte' },
    { key: 'preco', example: 'R$ 50,00' },
    { key: 'atendente', example: 'Maria' },
    { key: 'data', example: '05/04/2024' },
    { key: 'hora', example: '14:30' },
    { key: 'data_hora', example: 'sexta, 05 de abril de 2024, às 14:30' },
];

export function WhatsappEstabelecimento() {
    const { whatsappSchedulingConfirmationMessage, setWhatsappSchedulingConfirmationMessage } = useEstabFormContext();

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        setWhatsappSchedulingConfirmationMessage((prev) => prev + emojiData.emoji);
    };

    useEffect(() => {
        if (!showEmojiPicker) return;

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (emojiPickerRef.current?.contains(target)) return;
            setShowEmojiPicker(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown, { passive: true });

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [showEmojiPicker]);

    return (
        <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
            <SettingsSection
                title="Mensagem de confirmação"
                footer="Enviada pelo WhatsApp quando um agendamento é confirmado."
            >
                <SettingsRow stacked>
                    <div className="relative w-full" ref={emojiPickerRef}>
                        <textarea
                            id="whatsappSchedulingConfirmationMessage"
                            aria-label="Mensagem de confirmação do agendamento"
                            className="textarea estab-field !ml-0 !min-h-[11rem] !w-full pr-10"
                            placeholder="✅ Agendamento realizado com sucesso!"
                            value={whatsappSchedulingConfirmationMessage}
                            onChange={(e) => setWhatsappSchedulingConfirmationMessage(e.target.value)}
                        />
                        <button
                            type="button"
                            className="absolute right-1.5 top-1.5 btn btn-ghost btn-xs btn-circle text-base-content/60"
                            onClick={() => setShowEmojiPicker((prev) => !prev)}
                            aria-label="Abrir emojis"
                        >
                            <Smile aria-hidden="true" />
                        </button>
                        {showEmojiPicker && (
                            <div className="absolute right-0 z-50 mt-2 max-w-full">
                                <EmojiPicker onEmojiClick={handleEmojiClick} lazyLoadEmojis skinTonesDisabled />
                            </div>
                        )}
                    </div>
                </SettingsRow>
            </SettingsSection>

            <SettingsSection title="Variáveis" footer="Toque em uma variável para inseri-la na mensagem.">
                {MESSAGE_VARIABLES.map(({ key, example }) => (
                    <SettingsRow key={key} inline>
                        <button
                            type="button"
                            className="estab-variable text-left hover:text-[rgb(var(--sf-nav-accent-rgb))]"
                            onClick={() => setWhatsappSchedulingConfirmationMessage((prev) => prev + `{{${key}}}`)}
                            aria-label={`Inserir variável ${key}`}
                        >
                            {`{{${key}}}`}
                        </button>
                        <span className="truncate text-right text-xs text-base-content/55">{example}</span>
                    </SettingsRow>
                ))}
            </SettingsSection>
        </div>
    );
}
